import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { WsStatusInfo } from '../../api/websocket';
import './ConnectionDot.scss';

interface Props {
  statusInfo: WsStatusInfo | null;
}

function formatAgo(ts: number | null, neverLabel: string): string {
  if (ts === null) return neverLabel;
  const sec = Math.floor((Date.now() - ts) / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  return `${min}m ${sec % 60}s`;
}

export const ConnectionDot: React.FC<Props> = ({ statusInfo }) => {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);
  const [, setTick] = useState(0);

  // refresh tooltip every second while visible so "N sec ago" stays current
  useEffect(() => {
    if (!hovered) return;
    const id = setInterval(() => setTick(v => v + 1), 1000);
    return () => clearInterval(id);
  }, [hovered]);

  const status = statusInfo?.status ?? 'connecting';

  return (
    <div
      className={`conn-dot conn-dot--${status}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label={t(`connDot.status.${status}`)}
    >
      {hovered && (
        <div className="conn-dot__tooltip">
          <div className="conn-dot__tooltip-row conn-dot__tooltip-status">
            <span className={`conn-dot__tooltip-indicator conn-dot__tooltip-indicator--${status}`} />
            {t(`connDot.status.${status}`)}
            {status === 'reconnecting' && statusInfo && (
              <span className="conn-dot__tooltip-dim">
                {' '}({statusInfo.reconnectAttempt + 1}/5)
              </span>
            )}
          </div>
          <div className="conn-dot__tooltip-row conn-dot__tooltip-dim">
            {t('connDot.lastMessage')}:{' '}
            <span className="conn-dot__tooltip-value">
              {formatAgo(statusInfo?.lastMessageAt ?? null, t('connDot.never'))}
            </span>
          </div>
          <div className="conn-dot__tooltip-row conn-dot__tooltip-dim">
            {t('connDot.msgCount')}:{' '}
            <span className="conn-dot__tooltip-value">
              {(statusInfo?.messageCount ?? 0).toLocaleString()}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
